import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE } from './config';
import socket from '../socket/socket';
import '../css/AddCourt.css';

const COURT_NUMBERS = Array.from({ length: 18 }, (_, index) => index + 1);

const normalizeStatus = status =>
  String(status || 'OPEN').trim().toUpperCase();

function AddCourt({ groupId, onAdded, onDeleted, onChanged }) {
  const [existingCourts, setExistingCourts] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingCourtNo, setDeletingCourtNo] = useState(null);
  const [message, setMessage] = useState(null);

  const loadCourts = useCallback(async () => {
    if (!groupId) return;

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE}/api/courts/${groupId}`);
      const data = await response.json().catch(() => []);

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      const courtRows = Array.isArray(data) ? data : [];
      const numbers = courtRows
        .map(court => Number(court.court_no))
        .filter(number => Number.isInteger(number));

      setExistingCourts(courtRows);
      setSelectedNumbers(current =>
        current.filter(number => !numbers.includes(number))
      );
    } catch (error) {
      setMessage({ type: 'error', text: `โหลด Court ไม่สำเร็จ: ${error.message}` });
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    setSelectedNumbers([]);
    setMessage(null);
    loadCourts();
  }, [groupId, loadCourts]);

  useEffect(() => {
    if (!groupId) return undefined;

    const handleCourtsUpdated = data => {
      if (Number(data?.groupId) !== Number(groupId)) return;
      loadCourts();
    };

    socket.emit('join-group', Number(groupId));
    socket.on('courts-initialized', handleCourtsUpdated);
    socket.on('court-status-updated', handleCourtsUpdated);

    return () => {
      socket.off('courts-initialized', handleCourtsUpdated);
      socket.off('court-status-updated', handleCourtsUpdated);
    };
  }, [groupId, loadCourts]);

  const existingMap = useMemo(
    () => new Map(
      existingCourts.map(court => [Number(court.court_no), court])
    ),
    [existingCourts]
  );

  const existingSet = useMemo(
    () => new Set(existingMap.keys()),
    [existingMap]
  );

  const selectedSet = useMemo(
    () => new Set(selectedNumbers),
    [selectedNumbers]
  );

  const availableNumbers = COURT_NUMBERS.filter(
    number => !existingSet.has(number)
  );

  const toggleCourt = courtNo => {
    if (existingSet.has(courtNo) || submitting || deletingCourtNo !== null) return;

    setMessage(null);
    setSelectedNumbers(current =>
      current.includes(courtNo)
        ? current.filter(number => number !== courtNo)
        : [...current, courtNo].sort((a, b) => a - b)
    );
  };

  const addSelectedCourts = async () => {
    if (
      !groupId ||
      selectedNumbers.length === 0 ||
      submitting ||
      deletingCourtNo !== null
    ) return;

    try {
      setSubmitting(true);
      setMessage(null);

      const response = await fetch(
        `${API_BASE}/api/groups/${groupId}/courts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ courtNumbers: selectedNumbers })
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      setMessage({ type: 'success', text: data.message || 'เพิ่ม Court สำเร็จ' });
      setSelectedNumbers([]);
      await loadCourts();

      const changePayload = {
        groupId: Number(groupId),
        action: 'added',
        courtNumbers: data.addedCourtNumbers || selectedNumbers
      };
      window.dispatchEvent(
        new CustomEvent('courts-changed', { detail: changePayload })
      );

      if (onAdded) await onAdded(data);
      if (onChanged) await onChanged(changePayload);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCourt = async court => {
    const courtNo = Number(court?.court_no);
    const status = normalizeStatus(court?.status);

    if (!court?.id || deletingCourtNo !== null) return;

    if (status !== 'OPEN') {
      setMessage({
        type: 'error',
        text: `Court ${courtNo} กำลังใช้งาน ต้อง Check-out ก่อนจึงจะลบได้`
      });
      return;
    }

    if (!window.confirm(`ยืนยันลบ Court ${courtNo} ใช่หรือไม่?`)) return;

    try {
      setDeletingCourtNo(courtNo);
      setMessage(null);

      const response = await fetch(`${API_BASE}/api/courts/${court.id}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      setSelectedNumbers(current =>
        current.filter(number => number !== courtNo)
      );
      setMessage({
        type: 'success',
        text: data.message || `ลบ Court ${courtNo} สำเร็จ`
      });
      await loadCourts();

      const changePayload = {
        groupId: Number(groupId),
        action: 'deleted',
        courtNo,
        courtId: court.id
      };
      window.dispatchEvent(
        new CustomEvent('courts-changed', { detail: changePayload })
      );

      if (onDeleted) await onDeleted(changePayload);
      if (onChanged) await onChanged(changePayload);
    } catch (error) {
      setMessage({ type: 'error', text: `ลบ Court ไม่สำเร็จ: ${error.message}` });
    } finally {
      setDeletingCourtNo(null);
    }
  };

  return (
    <section className="ac-card">
      <div className="ac-header">
        <div>
          <span className="ac-eyebrow">COURT SETUP</span>
          <h2>🎟 เลือกหมายเลขคอร์ท</h2>
          <p>เลือกคอร์ทหมายเลขใดก่อนก็ได้ ตั้งแต่ 1–18</p>
        </div>

        <div className="ac-counter">
          <strong>{selectedNumbers.length}</strong>
          <span>เลือกแล้ว</span>
        </div>
      </div>

      <div className="ac-legend">
        <span><i className="available" /> เลือกได้</span>
        <span><i className="selected" /> กำลังเลือก</span>
        <span><i className="existing" /> มีอยู่แล้ว (ลบได้เมื่อ OPEN)</span>
      </div>

      <div className="ac-court-grid" role="group" aria-label="เลือกหมายเลข Court 1 ถึง 18">
        {COURT_NUMBERS.map(courtNo => {
          const existingCourt = existingMap.get(courtNo);
          const existing = Boolean(existingCourt);
          const selected = selectedSet.has(courtNo);
          const status = normalizeStatus(existingCourt?.status);
          const canDelete = existing && status === 'OPEN';

          return (
            <div
              key={courtNo}
              className={`ac-court-slot ${existing ? 'has-court' : ''}`}
            >
              <button
                type="button"
                className={`ac-court-card ${
                  existing ? 'existing' : selected ? 'selected' : 'available'
                }`}
                disabled={existing || submitting || deletingCourtNo !== null}
                aria-pressed={selected}
                onClick={() => toggleCourt(courtNo)}
              >
                <span className="ac-court-card-label">COURT</span>
                <strong>{courtNo}</strong>
                <span className="ac-court-card-state">
                  {existing
                    ? status
                    : selected
                      ? '✓ เลือก'
                      : '＋ เพิ่ม'}
                </span>
              </button>

              {existing && (
                <button
                  type="button"
                  className={`ac-court-delete ${canDelete ? '' : 'locked'}`}
                  disabled={
                    submitting ||
                    deletingCourtNo !== null ||
                    !canDelete
                  }
                  title={
                    canDelete
                      ? `ลบ Court ${courtNo}`
                      : `Court ${courtNo} กำลังใช้งาน ต้อง Check-out ก่อน`
                  }
                  aria-label={`ลบ Court ${courtNo}`}
                  onClick={() => deleteCourt(existingCourt)}
                >
                  {deletingCourtNo === courtNo ? '…' : canDelete ? '🗑' : '🔒'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {loading && <div className="ac-loading">กำลังตรวจสอบ Court...</div>}

      {message && (
        <div className={`ac-message ${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      <div className="ac-actions">
        <button
          type="button"
          className="ac-secondary-button"
          disabled={
            availableNumbers.length === 0 ||
            submitting ||
            deletingCourtNo !== null
          }
          onClick={() => {
            setSelectedNumbers(
              selectedNumbers.length === availableNumbers.length
                ? []
                : availableNumbers
            );
            setMessage(null);
          }}
        >
          {selectedNumbers.length === availableNumbers.length &&
          availableNumbers.length > 0
            ? 'ยกเลิกทั้งหมด'
            : 'เลือกคอร์ทที่ว่างทั้งหมด'}
        </button>

        <button
          type="button"
          className="ac-add-button"
          disabled={
            selectedNumbers.length === 0 ||
            submitting ||
            deletingCourtNo !== null
          }
          onClick={addSelectedCourts}
        >
          {submitting
            ? 'กำลังเพิ่ม...'
            : selectedNumbers.length > 0
              ? `＋ เพิ่ม Court ${selectedNumbers.join(', ')}`
              : 'เลือกหมายเลขที่ต้องการเพิ่ม'}
        </button>
      </div>

      {availableNumbers.length === 0 && !loading && (
        <div className="ac-complete">🎉 มี Court ครบ 1–18 แล้ว</div>
      )}
    </section>
  );
}

export default AddCourt;
